/**
 * AppHeader - Shared header component for landing page and game room
 *
 * Features MTG-inspired styling with glass-morphism and purple accents.
 */

import { useAuth } from '@/contexts/AuthContext'
import {
  ArrowLeft,
  Check,
  Copy,
  Dices,
  Gamepad2,
  ListChecks,
  LogIn,
  LogOut,
  Menu,
  Palette,
  Search,
  Settings,
  Swords,
} from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/components/avatar'
import { Button } from '@repo/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@repo/ui/components/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@repo/ui/components/sheet'

import type { MtgColorTheme } from '../contexts/ThemeContext.js'
import logoBlue from '../assets/logo_1024_blue.png'
import logoDeath from '../assets/logo_1024_death.png'
import logoFire from '../assets/logo_1024_fire.png'
import logoGreen from '../assets/logo_1024_green.png'
import logoWarmGold from '../assets/logo_1024_warmgold.png'
import logo from '../assets/logo_1024x1024.png'
import { MTG_THEMES, useTheme } from '../contexts/ThemeContext.js'
import { useSearchShortcutParts } from '../hooks/useSearchShortcut.js'
import { ThemePickerInline, ThemeToggle } from './ThemeToggle.js'

// ============================================================================
// Types
// ============================================================================

function scrollToSection(
  event: React.MouseEvent<HTMLAnchorElement>,
  targetId: string,
) {
  event.preventDefault()
  document
    .getElementById(targetId)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

interface NavItem {
  label: string
  targetId: string
}

interface AppHeaderProps {
  /** Header variant - determines which elements are shown */
  variant: 'landing' | 'game'

  // Landing-specific props
  /** Navigation items for smooth scroll (landing only) */
  navItems?: NavItem[]
  /** Callback when sign in is clicked (landing only) */
  onSignIn?: () => void

  // Game-specific props
  /** Shareable link to display (game only) */
  shareLink?: string
  /** Whether the link was recently copied (game only) */
  copied?: boolean
  /** Callback when leave button is clicked (game only) */
  onLeave?: () => void
  /** Callback when copy link is clicked (game only) */
  onCopyLink?: () => void
  /** Callback when settings button is clicked (game only) */
  onOpenSettings?: () => void
  /** Opens the per-room gameplay settings (game variant only, not this device) */
  onOpenGameplaySettings?: () => void
  /** Callback when search button is clicked (game only) */
  onSearchClick?: () => void
  /** Opens the minigames dialog (game only) */
  onMinigamesClick?: () => void
  /** Whether the commanders panel is open (game only) */
  commandersPanelOpen?: boolean
  /** Callback to toggle commanders panel (game only) */
  onCommandersPanelToggle?: () => void
  /** Shortcut parts for commanders panel, e.g. ['⌘', 'M'] (game only) */
  commanderShortcutParts?: string[]
  /** Opens the decklist dialog (game only) */
  onDecklistClick?: () => void
  /** How many cards this player has pasted, for the button's badge */
  decklistCount?: number
}

const EMPTY_NAV_ITEMS: NavItem[] = []

// ============================================================================
// Shared Components
// ============================================================================

function Logo({ size = 'default' }: { size?: 'default' | 'small' }) {
  const { mtgTheme } = useTheme()
  const sizeClasses = size === 'small' ? 'h-8 w-8' : 'h-10 w-10'
  const textClasses = size === 'small' ? 'text-lg' : 'text-xl'
  const roundedClasses = size === 'small' ? 'rounded-lg' : 'rounded-xl'
  // Use theme-specific logos
  const logoSrc =
    mtgTheme === 'white'
      ? logoWarmGold
      : mtgTheme === 'red'
        ? logoFire
        : mtgTheme === 'blue'
          ? logoBlue
          : mtgTheme === 'black'
            ? logoDeath
            : mtgTheme === 'green'
              ? logoGreen
              : logo

  return (
    <div className="flex items-center gap-2">
      <img
        src={logoSrc}
        alt="Spelltablez Logo"
        className={` ${sizeClasses} ${roundedClasses} object-contain`}
      />
      <span className={` ${textClasses} text-text-primary font-bold`}>
        Spelltablez
      </span>
    </div>
  )
}

function UserMenu() {
  const { user, signOut } = useAuth()

  if (!user) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="text-text-secondary hover:text-text-primary flex items-center gap-2"
          title={user.username}
          data-testid="header-user-menu"
        >
          <Avatar className="size-8 shrink-0">
            <AvatarImage src={user.avatar || undefined} />
            <AvatarFallback className="bg-brand text-white">
              {user.username.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="hidden max-w-32 truncate lg:inline">
            {user.username}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="border-surface-3 bg-surface-1"
      >
        <DropdownMenuItem
          onClick={signOut}
          className="text-text-secondary focus:bg-surface-2 focus:text-text-primary cursor-pointer"
        >
          <LogOut className="mr-2 size-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SettingsDropdown({
  onOpenSettings,
  onOpenGameplaySettings,
}: {
  onOpenSettings?: () => void
  onOpenGameplaySettings?: () => void
}) {
  const { mtgTheme, setMtgTheme } = useTheme()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-text-muted hover:text-text-primary"
          title="Audio & video settings"
          data-testid="settings-button"
        >
          <Settings className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="border-surface-3 bg-surface-1 w-56"
      >
        {onOpenSettings && (
          <DropdownMenuItem
            onClick={onOpenSettings}
            className="text-text-secondary focus:bg-surface-2 focus:text-text-primary cursor-pointer"
          >
            <Settings className="mr-2 size-4" />
            Setup Audio & Video
          </DropdownMenuItem>
        )}
        {onOpenGameplaySettings && (
          <DropdownMenuItem
            onClick={onOpenGameplaySettings}
            className="text-text-secondary focus:bg-surface-2 focus:text-text-primary cursor-pointer"
            data-testid="gameplay-settings-menu-item"
          >
            <Dices className="mr-2 size-4" />
            Gameplay settings
          </DropdownMenuItem>
        )}
        <DropdownMenuLabel className="text-text-muted flex items-center gap-1 text-xs font-normal">
          <Palette className="size-3" />
          Theme
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={mtgTheme}
          onValueChange={(value) => setMtgTheme(value as MtgColorTheme)}
        >
          {(Object.keys(MTG_THEMES) as MtgColorTheme[]).map((key) => {
            const theme = MTG_THEMES[key]
            const iconSvg = theme.iconSvg
            const iconColor = theme.iconColor
            return (
              <DropdownMenuRadioItem
                key={key}
                value={key}
                className="text-text-secondary focus:bg-surface-2 focus:text-foreground cursor-pointer"
              >
                <span className="mr-2 flex w-4 items-center justify-center">
                  {iconSvg && iconColor ? (
                    <span
                      className="size-4 shrink-0 overflow-hidden [&_svg]:block [&_svg]:size-full"
                      style={{ color: iconColor }}
                      title={`${theme.label} mana`}
                      dangerouslySetInnerHTML={{ __html: iconSvg }}
                    />
                  ) : (
                    <Settings className="size-4" aria-label="Default theme" />
                  )}
                </span>
                <span className="flex-1">{theme.label}</span>
                {key !== 'none' && (
                  <span className="text-text-muted ml-2 hidden text-xs sm:inline">
                    {theme.description.split(',')[0]}
                  </span>
                )}
              </DropdownMenuRadioItem>
            )
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ============================================================================
// Landing Header
// ============================================================================

function LandingHeader({
  navItems = EMPTY_NAV_ITEMS,
  onSignIn,
}: AppHeaderProps) {
  const { user, isLoading: isAuthLoading, signOut } = useAuth()
  const isAuthenticated = !!user

  return (
    <header className="border-border-muted bg-surface-0/80 sticky top-0 z-50 border-b backdrop-blur-md">
      {/* Subtle gradient border effect */}
      <div className="bg-linear-to-r via-brand/30 absolute inset-x-0 bottom-0 h-px from-transparent to-transparent" />

      <div className="container relative mx-auto flex items-center justify-between p-4">
        <Logo />

        {/* Desktop Navigation */}
        <nav className="hidden items-center gap-6 lg:flex">
          {navItems.map((item) => (
            <a
              key={item.targetId}
              href={`#${item.targetId}`}
              onClick={(e) => scrollToSection(e, item.targetId)}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              {item.label}
            </a>
          ))}

          <ThemeToggle />

          {/* Auth section */}
          {isAuthLoading ? (
            <div className="bg-surface-2 h-9 w-24 animate-pulse rounded-md" />
          ) : isAuthenticated ? (
            <UserMenu />
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={onSignIn}
              className="border-brand/50 text-brand-muted-foreground hover:bg-brand/20 hover:text-brand-muted-foreground gap-2"
            >
              <LogIn className="size-4" />
              <span>Sign in with Discord</span>
            </Button>
          )}
        </nav>

        {/* Mobile Menu */}
        <div className="lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open navigation menu"
                className="text-text-secondary hover:bg-surface-2 hover:text-text-primary"
              >
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="border-border-muted bg-surface-0/95 flex w-full flex-col border-l p-0 backdrop-blur-xl sm:max-w-sm"
              onInteractOutside={(e) => {
                const target = e.target as HTMLElement
                if (target.closest('[data-slot="dropdown-menu-content"]')) {
                  e.preventDefault()
                }
              }}
            >
              <SheetHeader className="border-border-muted border-b px-6 py-4">
                <div className="flex items-center gap-2">
                  <Logo size="small" />
                </div>
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              </SheetHeader>

              <div className="flex flex-1 flex-col gap-1 px-4 py-6">
                {navItems.map((item) => (
                  <a
                    key={item.targetId}
                    href={`#${item.targetId}`}
                    onClick={(e) => scrollToSection(e, item.targetId)}
                    className="text-text-secondary hover:bg-surface-2 hover:text-text-primary flex items-center rounded-lg px-4 py-3 text-lg font-medium transition-colors"
                  >
                    {item.label}
                  </a>
                ))}

                {/* Theme picker in mobile menu (inline — avoids dropdown-in-sheet issues) */}
                <ThemePickerInline />
              </div>

              <div className="border-border-muted border-t p-6">
                {isAuthLoading ? (
                  <div className="bg-surface-2 h-12 w-full animate-pulse rounded-lg" />
                ) : isAuthenticated && user ? (
                  <div className="flex flex-col gap-4">
                    <div className="border-border-muted bg-surface-1/50 flex items-center gap-3 rounded-xl border p-3">
                      <Avatar className="border-surface-3 size-10 border">
                        <AvatarImage src={user.avatar || undefined} />
                        <AvatarFallback className="bg-brand text-white">
                          {user.username.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <span className="text-text-primary font-medium">
                          {user.username}
                        </span>
                        <span className="text-text-muted text-xs">
                          Logged in
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={signOut}
                      className="border-surface-3 text-text-secondary hover:bg-surface-2 hover:text-text-primary h-12 w-full justify-center gap-2"
                    >
                      <LogOut className="size-4" />
                      Sign Out
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="lg"
                    onClick={onSignIn}
                    className="bg-linear-to-r group relative h-14 w-full gap-3 overflow-hidden border border-white/10 from-[#5865F2] to-purple-600 text-lg font-semibold text-white shadow-[0_0_30px_rgba(88,101,242,0.4)] transition-all hover:scale-105 hover:from-[#4752C4] hover:to-purple-700 hover:shadow-[0_0_50px_rgba(88,101,242,0.6)]"
                  >
                    <div className="absolute inset-0 bg-white/20 opacity-0 transition-opacity group-hover:opacity-100" />
                    <svg
                      className="size-6 transition-transform group-hover:scale-110"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                    </svg>
                    Sign in with Discord
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}

// ============================================================================
// Game Header
// ============================================================================

function GameHeader({
  shareLink,
  copied = false,
  onLeave,
  onCopyLink,
  onOpenSettings,
  onOpenGameplaySettings,
  onSearchClick,
  onMinigamesClick,
  commandersPanelOpen = false,
  onCommandersPanelToggle,
  onDecklistClick,
  decklistCount,
  commanderShortcutParts,
}: AppHeaderProps) {
  const shortcut = useSearchShortcutParts()
  return (
    <header className="border-surface-2 bg-surface-1/80 shrink-0 border-b backdrop-blur-md">
      {/* Subtle gradient border effect */}
      <div className="bg-linear-to-r via-brand/20 absolute inset-x-0 bottom-0 h-px from-transparent to-transparent" />

      <div className="relative flex items-center justify-between px-4 py-3">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onLeave}
            className="text-text-muted hover:text-text-primary"
            title="Leave game room"
            data-testid="leave-game-button"
          >
            <ArrowLeft className="mr-2 size-4" />
            Leave
          </Button>

          <div className="bg-surface-3 h-6 w-px" />

          <div className="hidden min-w-0 flex-1 items-center gap-2 sm:flex">
            <span className="text-text-muted shrink-0 text-sm">
              Share Link:
            </span>
            <button
              type="button"
              className="bg-surface-2 text-brand-muted-foreground hover:bg-surface-3 min-w-0 flex-1 cursor-pointer truncate rounded-sm px-2 py-1 text-left font-mono text-sm transition-colors"
              data-testid="game-id-display"
              onClick={onCopyLink}
              title="Click to copy shareable link"
            >
              {shareLink}
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCopyLink}
              className="text-text-muted hover:text-text-primary shrink-0"
              title="Copy shareable link"
              data-testid="copy-share-link-button"
            >
              {copied ? (
                <Check className="size-4" />
              ) : (
                <Copy className="size-4" />
              )}
            </Button>
          </div>

          {/* Mobile: Show only copy button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onCopyLink}
            className="text-text-muted hover:text-text-primary sm:hidden"
            title="Copy shareable link"
          >
            {copied ? (
              <Check className="size-4" />
            ) : (
              <Copy className="size-4" />
            )}
            <span className="ml-2">Copy Link</span>
          </Button>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3">
          {/* Commanders – global list, same style as Search */}
          {onCommandersPanelToggle && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCommandersPanelToggle}
              className={`border-surface-3 gap-2 ${
                commandersPanelOpen
                  ? 'bg-surface-3 text-text-primary'
                  : `bg-surface-2 text-text-muted hover:bg-surface-3 hover:text-text-primary`
              } `}
              title={
                commanderShortcutParts
                  ? `View and edit commanders list (${commanderShortcutParts.join('')})`
                  : 'View and edit commanders list'
              }
              data-testid="commanders-panel-button"
            >
              <Swords className="size-4" />
              <span className="hidden sm:inline">Commanders</span>
              {commanderShortcutParts && commanderShortcutParts.length > 0 && (
                <kbd className="bg-surface-3 text-text-muted pointer-events-none hidden h-5 select-none items-center gap-1 rounded-sm border px-1.5 font-mono text-[10px] font-medium opacity-75 sm:inline-flex">
                  {commanderShortcutParts.map((part) => (
                    <span key={part} className="text-xs">
                      {part}
                    </span>
                  ))}
                </kbd>
              )}
            </Button>
          )}

          {/* Decklist - between Commanders and Search on purpose. All three
              are about which cards are in play, and this one is read by
              recognition rather than by a person, which is the least obvious
              of the three and so the easiest to never find. */}
          {onDecklistClick && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDecklistClick}
              className="border-surface-3 bg-surface-2 text-text-muted hover:bg-surface-3 hover:text-text-primary gap-2"
              title={
                decklistCount && decklistCount > 0
                  ? `${decklistCount} cards - clicking your cards searches only these`
                  : 'Paste your decklist to make card recognition much more accurate'
              }
              data-testid="decklist-button"
            >
              <ListChecks className="size-4" />
              <span className="hidden sm:inline">Decklist</span>
              {decklistCount && decklistCount > 0 ? (
                <span
                  className="bg-surface-3 text-text-secondary pointer-events-none hidden h-5 select-none items-center rounded-sm border px-1.5 font-mono text-[10px] font-medium sm:inline-flex"
                  data-testid="decklist-button-count"
                >
                  {decklistCount}
                </span>
              ) : null}
            </Button>
          )}

          {/* Search Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={onSearchClick}
            className="border-surface-3 bg-surface-2 text-text-muted hover:bg-surface-3 hover:text-text-primary gap-2"
            title={`Search cards (${shortcut.modifier}${shortcut.modifier.length === 1 ? '' : '+'}${shortcut.key})`}
            data-testid="search-button"
          >
            <Search className="size-4" />
            <span className="hidden sm:inline">Search cards</span>
            <kbd className="bg-surface-3 text-text-muted pointer-events-none hidden h-5 select-none items-center gap-1 rounded-sm border px-1.5 font-mono text-[10px] font-medium opacity-75 sm:inline-flex">
              <span className="text-xs">{shortcut.modifier}</span>
              {shortcut.key}
            </kbd>
          </Button>

          {/* Last of the group and next to the cog on purpose. Commanders,
              Decklist and Search all answer "which cards are in play"; this
              one does not, so it sits after them rather than splitting them
              up. Behind the cog it would be invisible, and the three-card
              chooser's "None of these" already taught this table what a
              control nobody finds is worth. */}
          {onMinigamesClick && (
            <Button
              variant="outline"
              size="sm"
              onClick={onMinigamesClick}
              className="border-surface-3 bg-surface-2 text-text-muted hover:bg-surface-3 hover:text-text-primary gap-2"
              title="Play a minigame between turns"
              data-testid="minigames-button"
            >
              <Gamepad2 className="size-4" />
              <span className="hidden sm:inline">Mini games</span>
            </Button>
          )}

          <SettingsDropdown
            onOpenSettings={onOpenSettings}
            onOpenGameplaySettings={onOpenGameplaySettings}
          />

          <UserMenu />
        </div>
      </div>
    </header>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function AppHeader(props: AppHeaderProps) {
  if (props.variant === 'landing') {
    return <LandingHeader {...props} />
  }
  return <GameHeader {...props} />
}
